import { Volume2, VolumeX, Loader2, AlertCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '../../lib/utils'
import type { MonitorConnectionState } from './useCallMonitor'

interface ListenButtonProps {
  isListening: boolean
  connectionState: MonitorConnectionState
  audioLevel: number
  hasUrl: boolean
  onToggle: () => void
}

export function ListenButton({
  isListening,
  connectionState,
  audioLevel,
  hasUrl,
  onToggle,
}: ListenButtonProps) {
  const { t } = useTranslation('calls')
  // When listening, the active connectionState drives the visual (live vs connecting vs error).
  // When not listening, the button is the idle "start" control.
  const mode: 'idle' | 'connecting' | 'live' | 'error' = !isListening
    ? 'idle'
    : connectionState === 'connected'
      ? 'live'
      : connectionState === 'error'
        ? 'error'
        : 'connecting'

  const disabled = mode === 'idle' && !hasUrl
  const helper = mode === 'idle' && !hasUrl ? t('listen.waitingStream') : null

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={onToggle}
        disabled={disabled}
        aria-pressed={isListening}
        title={helper ?? undefined}
        className={cn(
          'inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
          mode === 'idle' && !disabled && 'bg-surface-elevated text-text-primary hover:bg-surface-elevated/70',
          mode === 'idle' && disabled && 'cursor-not-allowed bg-surface-elevated text-text-muted',
          mode === 'connecting' && 'bg-info/15 text-info',
          mode === 'live' && 'bg-success/20 text-success hover:bg-success/30',
          mode === 'error' && 'bg-error/15 text-error hover:bg-error/25',
        )}
      >
        {mode === 'idle' && <VolumeX className="h-4 w-4" />}
        {mode === 'connecting' && <Loader2 className="h-4 w-4 animate-spin" />}
        {mode === 'live' && <Volume2 className="h-4 w-4" />}
        {mode === 'error' && <AlertCircle className="h-4 w-4" />}
        <span>
          {mode === 'idle' && t('listen.listenIn')}
          {mode === 'connecting' && t('listen.connecting')}
          {mode === 'live' && t('listen.stopListening')}
          {mode === 'error' && t('listen.retryListen')}
        </span>
        {mode === 'live' && (
          <span
            className="h-2 w-10 overflow-hidden rounded-full bg-success/20"
            aria-hidden
          >
            <span
              className="block h-full bg-success transition-[width] duration-100"
              style={{ width: `${Math.min(Math.max(audioLevel * 140, 4), 100)}%` }}
            />
          </span>
        )}
      </button>
      {helper && <span className="text-xs text-text-muted">{helper}</span>}
    </div>
  )
}
